// =============================================================================
// FILE: packages/renderer/src/components/public/DataPacketJourney.tsx
// PURPOSE: "Experience a Data Packet Journey" — an interactive learning/demo of
//   how data travels through real telecom infrastructure. The user presses Start;
//   R2 generates a packet that animates along the full path (device → CAT6A →
//   outlet → cable tray → telecom room → patch panel → switch → fiber → carrier →
//   cloud → destination) with live node explanations and playback controls.
//   Professional engineering visualization — not a passive video.
// =============================================================================

import { useEffect, useRef, useState, type JSX, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import {
  Play, Pause, RotateCcw, X, Rocket, Laptop, Cable, PlugZap, Rows3, Building2,
  LayoutGrid, Network, Zap, RadioTower, Cloud, Server, Printer,
} from 'lucide-react';

interface Hop { id: string; label: string; desc: string; icon: ComponentType<{ className?: string; style?: React.CSSProperties }> }

const HOPS: Hop[] = [
  { id: 'device', label: 'Your Device', desc: 'A laptop or workstation creates a data packet and sends it to the network.', icon: Laptop },
  { id: 'cat6a', label: 'CAT6A Cable', desc: 'The packet travels as electrical signals over shielded twisted-pair copper — up to 10 Gbps.', icon: Cable },
  { id: 'outlet', label: 'Wall Outlet', desc: 'An RJ45 work-area outlet — the structured-cabling termination at the desk.', icon: PlugZap },
  { id: 'tray', label: 'Cable Tray', desc: 'Bundled cables run through overhead trays and pathways toward the telecom room.', icon: Rows3 },
  { id: 'tr', label: 'Telecom Room', desc: 'The floor Telecommunications Room (TR) — where horizontal cabling converges.', icon: Building2 },
  { id: 'patch', label: 'Patch Panel', desc: 'Cables terminate on a patch panel; a patch cord cross-connects to active gear.', icon: LayoutGrid },
  { id: 'switch', label: 'Network Switch', desc: 'The switch reads the packet and forwards it toward its destination.', icon: Network },
  { id: 'fiber', label: 'Fiber Uplink', desc: 'The packet converts to light and races along the fiber backbone uplink.', icon: Zap },
  { id: 'carrier', label: 'Carrier / ISP', desc: 'It enters the service-provider network and is routed across the internet.', icon: RadioTower },
  { id: 'cloud', label: 'Cloud', desc: 'Global cloud routing directs the packet toward the hosting region.', icon: Cloud },
  { id: 'dest', label: 'Destination', desc: 'The destination server receives the packet — journey complete.', icon: Server },
];

type Speed = 'slow' | 'normal' | 'fast';
const SPEED_MS: Record<Speed, number> = { slow: 20000, normal: 13000, fast: 8000 };

const VB_W = 1240, VB_H = 260;
const X0 = 70, X1 = 1170, SPINE_Y = 150;
const XS = HOPS.map((_, i) => X0 + ((X1 - X0) * i) / (HOPS.length - 1));

function printJourney(): void {
  const now = new Date().toLocaleString();
  const nodeSvg = HOPS.map((h, i) => {
    const x = XS[i]!;
    return `
      <circle cx="${x}" cy="${SPINE_Y}" r="6" fill="#0ea5e9" stroke="#0284c7" stroke-width="1.5"/>
      <line x1="${x}" y1="112" x2="${x}" y2="${SPINE_Y - 8}" stroke="#94a3b8" stroke-width="1"/>
      <circle cx="${x}" cy="100" r="15" fill="#f0f9ff" stroke="#334155" stroke-width="1"/>
      <text x="${x}" y="105" text-anchor="middle" font-family="Georgia,serif" font-size="10" font-weight="700" fill="#0f172a">${i + 1}</text>
      <text x="${x}" y="${SPINE_Y + 22}" text-anchor="middle" font-family="Arial,sans-serif" font-size="8.5" font-weight="700" fill="#0f172a">${h.label}</text>`;
  }).join('');
  const fArrows = XS.slice(0, -1).map((x, i) => {
    const mid = (x + XS[i + 1]!) / 2;
    return `<path d="M ${mid - 5} ${SPINE_Y - 5} L ${mid + 4} ${SPINE_Y} L ${mid - 5} ${SPINE_Y + 5} Z" fill="#0ea5e9"/>`;
  }).join('');
  const svg = `<svg viewBox="0 0 ${VB_W} 220" width="100%" xmlns="http://www.w3.org/2000/svg">
    <line x1="${X0}" y1="${SPINE_Y}" x2="${X1}" y2="${SPINE_Y}" stroke="#0ea5e9" stroke-width="2"/>
    ${fArrows}${nodeSvg}
    <text x="${X0}" y="50" font-family="Arial,sans-serif" font-size="11" font-weight="700" fill="#0f172a" letter-spacing="1">FORWARD PATH  &#8594;  DATA LEAVES YOUR DEVICE, TRAVELS TO THE DESTINATION</text>
    <text x="${X0}" y="200" font-family="Arial,sans-serif" font-size="11" font-weight="700" fill="#b45309" letter-spacing="1">RETURN PATH  &#8592;  THE RESPONSE TRAVELS BACK TO YOUR DEVICE</text>
  </svg>`;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>RCD² Engine — Data Packet Journey</title>
  <style>
    @page { size: A4 landscape; margin: 14mm; }
    body { font-family: Arial, Helvetica, sans-serif; color:#0f172a; margin:0; padding:24px; background:#fff; }
    .hdr { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #0f172a; padding-bottom:10px; }
    .brand { font-size:18px; font-weight:800; } .brand span { color:#0ea5e9; }
    .sub { font-size:10px; color:#64748b; text-transform:uppercase; letter-spacing:2px; margin-top:2px; }
    .meta { text-align:right; font-size:11px; color:#334155; line-height:1.6; }
    h2 { font-size:12px; text-transform:uppercase; letter-spacing:2px; color:#334155; margin:20px 0 8px; border-bottom:1px solid #cbd5e1; padding-bottom:4px; }
    .diagram { border:1px solid #cbd5e1; padding:12px; }
    .legend { display:flex; gap:24px; font-size:10px; color:#475569; margin-top:8px; }
    .legend i { display:inline-block; width:22px; height:3px; vertical-align:middle; margin-right:6px; }
    table { width:100%; border-collapse:collapse; font-size:11px; margin-top:6px; }
    td, th { border:1px solid #cbd5e1; padding:6px 8px; text-align:left; }
    th { background:#f1f5f9; font-size:9px; text-transform:uppercase; letter-spacing:1px; color:#475569; }
    .ftr { margin-top:22px; border-top:1px solid #cbd5e1; padding-top:8px; font-size:9px; color:#94a3b8; display:flex; justify-content:space-between; }
  </style></head><body>
    <div class="hdr">
      <div><div class="brand">RCD<span>²</span> ENGINE</div><div class="sub">BIM OPS Platform · Data Packet Journey</div></div>
      <div class="meta"><div><strong>Infrastructure Data Journey</strong></div><div>${now}</div></div>
    </div>
    <h2>The Journey of a Data Packet</h2>
    <div class="diagram">${svg}</div>
    <div class="legend">
      <span><i style="background:#0ea5e9"></i>Forward path (device → destination)</span>
      <span><i style="background:#b45309"></i>Return path (destination → device)</span>
    </div>
    <h2>Network Elements &amp; Sequence</h2>
    <table><thead><tr><th>#</th><th>Element</th><th>What happens here</th></tr></thead><tbody>
      ${HOPS.map((h, i) => `<tr><td>${i + 1}</td><td><strong>${h.label}</strong></td><td>${h.desc}</td></tr>`).join('')}
    </tbody></table>
    <div class="ftr"><span>RCD² Engine — Interactive infrastructure learning tool. Educational illustration of packet routing.</span><span>rcd2engine.com</span></div>
    <script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
  </body></html>`;
  const w = window.open('', '_blank', 'width=1100,height=800');
  if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  else {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (doc) { doc.open(); doc.write(html); doc.close(); }
    setTimeout(() => document.body.removeChild(iframe), 2000);
  }
}

function JourneyModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [started, setStarted] = useState(false);
  const [t, setT] = useState(0);            // 0..1 across the path
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>('normal');
  const rafRef = useRef<number | undefined>(undefined);
  const lastRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!playing) { lastRef.current = undefined; return; }
    let active = true;
    const dur = SPEED_MS[speed];
    const step = (now: number): void => {
      if (!active) return;
      if (lastRef.current == null) lastRef.current = now;
      const dt = now - lastRef.current; lastRef.current = now;
      setT((prev) => {
        const next = prev + (dt / dur) * 2;   // 2 units per full round trip
        return next >= 2 ? 2 : next;
      });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { active = false; if (rafRef.current) cancelAnimationFrame(rafRef.current); lastRef.current = undefined; };
  }, [playing, speed]);

  // Stop at the end of the round trip.
  useEffect(() => { if (t >= 2 && playing) setPlaying(false); }, [t, playing]);

  const start = (): void => { setStarted(true); setT(0); setPlaying(true); };
  const restart = (): void => { setT(0); setPlaying(true); };

  const forward = t <= 1;
  const f = forward ? t : 2 - t;                       // 0..1 traversal fraction
  const packetX = X0 + (X1 - X0) * f;
  const activeIdx = Math.min(HOPS.length - 1, Math.round(f * (HOPS.length - 1)));
  const active = HOPS[activeIdx]!;
  const ActiveIcon = active.icon;
  const flow = forward ? '#38bdf8' : '#fbbf24';

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 10060, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(3,6,11,0.82)', backdropFilter: 'blur(6px)' }} onClick={onClose}>
      <div data-testid="data-journey-modal" onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(1080px,100%)', maxHeight: '92vh', overflowY: 'auto', borderRadius: 18, background: 'linear-gradient(180deg,#0a1018,#0b1220)', border: '1px solid rgba(56,189,248,0.3)', boxShadow: '0 30px 90px rgba(0,0,0,0.65)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 22px', borderBottom: '1px solid rgba(56,189,248,0.16)' }}>
          <Rocket style={{ width: 18, height: 18, color: '#38bdf8' }} />
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#e6f6ff' }}>Data Packet Journey</div>
            <div style={{ fontSize: 10.5, color: 'rgba(148,163,184,0.75)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Interactive infrastructure demo</div>
          </div>
          <button type="button" data-testid="journey-close" onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'rgba(148,163,184,0.6)', cursor: 'pointer' }}><X style={{ width: 16, height: 16 }} /></button>
        </div>

        <div style={{ padding: '20px 22px 24px' }}>
          {!started ? (
            <div data-testid="journey-intro" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ width: 74, height: 74, margin: '0 auto', borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'radial-gradient(circle, rgba(56,189,248,0.22), rgba(56,189,248,0.04))', border: '1px solid rgba(56,189,248,0.5)' }}>
                <Rocket style={{ width: 30, height: 30, color: '#38bdf8' }} />
              </div>
              <h3 style={{ fontSize: 22, fontWeight: 800, color: '#e6f6ff', marginTop: 20 }}>Follow a packet across the network</h3>
              <p style={{ fontSize: 13.5, color: 'rgba(148,163,184,0.85)', maxWidth: 520, margin: '10px auto 0', lineHeight: 1.6 }}>
                Watch data leave your device and travel through the real telecommunications infrastructure — copper, trays,
                the telecom room, switches, fiber, the carrier network, and the cloud — all the way to its destination.
              </p>
              <button type="button" data-testid="journey-start-btn" onClick={start}
                style={{ marginTop: 26, padding: '13px 26px', borderRadius: 999, fontWeight: 800, fontSize: 15, cursor: 'pointer', color: '#04121b', background: 'linear-gradient(135deg,#38bdf8,#0ea5e9)', border: 'none', boxShadow: '0 8px 26px rgba(56,189,248,0.35)' }}>
                ▶ Start Data Journey
              </button>
            </div>
          ) : (
            <>
              {/* Diagram */}
              <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" style={{ display: 'block' }}>
                <line x1={X0} y1={SPINE_Y} x2={X1} y2={SPINE_Y} stroke="rgba(148,163,184,0.3)" strokeWidth={2} />
                {/* forward progress (blue) */}
                <line x1={X0} y1={SPINE_Y} x2={forward ? packetX : X1} y2={SPINE_Y} stroke="#38bdf8" strokeWidth={2.5} strokeLinecap="round" />
                {/* return progress (amber) */}
                {!forward && <line x1={packetX} y1={SPINE_Y} x2={X1} y2={SPINE_Y} stroke="#fbbf24" strokeWidth={2.5} strokeLinecap="round" />}
                {/* direction caption */}
                <text x={X0} y={44} fontSize={10} fontWeight={800} letterSpacing="0.16em" fill={flow}>
                  {forward ? 'FORWARD →  DATA LEAVING YOUR DEVICE' : '←  RETURN  RESPONSE COMING BACK'}
                </text>
                {/* nodes */}
                {HOPS.map((h, i) => {
                  const x = XS[i]!;
                  const reached = !forward || i <= activeIdx;
                  const isActive = i === activeIdx;
                  const Icon = h.icon;
                  return (
                    <g key={h.id} data-testid={`journey-node-${h.id}`}>
                      <line x1={x} y1={100} x2={x} y2={SPINE_Y - 7} stroke="rgba(148,163,184,0.25)" strokeWidth={1} />
                      <circle cx={x} cy={102} r={isActive ? 20 : 17}
                        fill={reached ? 'rgba(56,189,248,0.14)' : 'rgba(30,41,59,0.9)'}
                        stroke={isActive ? flow : reached ? '#38bdf8' : 'rgba(148,163,184,0.4)'} strokeWidth={isActive ? 2 : 1.4}
                        style={{ transition: 'all 0.25s' }} />
                      <foreignObject x={x - 11} y={91} width={22} height={22}>
                        <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                          <Icon style={{ width: 15, height: 15, color: reached ? '#7dd3fc' : 'rgba(148,163,184,0.7)' }} />
                        </div>
                      </foreignObject>
                      <circle cx={x} cy={SPINE_Y} r={5} fill={reached ? '#38bdf8' : 'rgba(30,41,59,0.95)'} stroke={reached ? '#38bdf8' : 'rgba(148,163,184,0.5)'} strokeWidth={1.4} />
                      <text x={x} y={SPINE_Y + 24} textAnchor="middle" fontSize={9.5} fontWeight={isActive ? 800 : 600}
                        fill={isActive ? '#e6f6ff' : 'rgba(148,163,184,0.7)'}>{h.label}</text>
                    </g>
                  );
                })}
                {/* trailing packets + head */}
                {[0.03, 0.015, 0].map((d, i) => {
                  const tf = forward ? Math.max(0, f - d) : Math.min(1, f + d);
                  const px = X0 + (X1 - X0) * tf;
                  return <circle key={i} cx={px} cy={SPINE_Y} r={i === 2 ? 6 : 3.5} fill={i === 2 ? '#ffffff' : flow} opacity={i === 2 ? 1 : 0.5} style={{ filter: `drop-shadow(0 0 6px ${flow})` }} />;
                })}
              </svg>

              {/* Active node info */}
              <div data-testid="journey-info" style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 8, padding: '14px 16px', borderRadius: 12, background: 'rgba(12,22,35,0.8)', border: '1px solid rgba(56,189,248,0.25)' }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: 'grid', placeItems: 'center', background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.4)' }}>
                  <ActiveIcon style={{ width: 22, height: 22, color: '#38bdf8' }} />
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: '#e6f6ff' }}>
                    <span style={{ color: 'rgba(148,163,184,0.6)', fontWeight: 600 }}>{forward ? 'Outbound' : 'Return'} · Step {activeIdx + 1}/{HOPS.length} · </span>{active.label}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'rgba(203,213,225,0.85)', marginTop: 3, lineHeight: 1.5 }}>{forward ? active.desc : `The response travels back through the ${active.label.toLowerCase()} on its way to your device.`}</div>
                </div>
              </div>

              {/* Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                <CtrlBtn testid="journey-play" onClick={() => (t >= 2 ? restart() : setPlaying((p) => !p))} active={playing}>
                  {playing ? <Pause style={{ width: 13, height: 13 }} /> : <Play style={{ width: 13, height: 13 }} />}
                  {t >= 2 ? 'Replay' : playing ? 'Pause' : 'Play'}
                </CtrlBtn>
                <CtrlBtn testid="journey-restart" onClick={restart}><RotateCcw style={{ width: 13, height: 13 }} /> Restart</CtrlBtn>
                <CtrlBtn testid="journey-print" onClick={printJourney}><Printer style={{ width: 13, height: 13 }} /> Print this Journey</CtrlBtn>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
                  <span style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(148,163,184,0.6)', marginRight: 2 }}>Speed</span>
                  {(['slow', 'normal', 'fast'] as Speed[]).map((s) => (
                    <CtrlBtn key={s} testid={`journey-speed-${s}`} onClick={() => setSpeed(s)} active={speed === s}>{s.charAt(0).toUpperCase() + s.slice(1)}</CtrlBtn>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CtrlBtn({ children, onClick, active, testid }: { children: React.ReactNode; onClick: () => void; active?: boolean; testid: string }): JSX.Element {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
        background: active ? 'rgba(56,189,248,0.16)' : 'rgba(30,41,59,0.6)',
        border: `1px solid ${active ? 'rgba(56,189,248,0.5)' : 'rgba(148,163,184,0.25)'}`,
        color: active ? '#7dd3fc' : 'rgba(203,213,225,0.85)' }}>
      {children}
    </button>
  );
}

export function DataPacketJourneyButton({ variant = 'hero' }: { variant?: 'hero' | 'compact' } = {}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      {variant === 'hero' ? (
        <button type="button" data-testid="data-journey-hero-btn" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-[13px] font-bold transition-all hover:-translate-y-0.5"
          style={{ background: 'rgba(56,189,248,0.14)', border: '1px solid rgba(56,189,248,0.5)', color: '#7dd3fc' }}>
          <Rocket className="h-4 w-4" /> Experience a Data Packet Journey
        </button>
      ) : (
        <button type="button" data-testid="data-journey-compact-btn" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold transition"
          style={{ color: '#7dd3fc' }}>
          <Rocket className="h-3.5 w-3.5" /> Experience a Data Packet Journey →
        </button>
      )}
      {open && <JourneyModal onClose={() => setOpen(false)} />}
    </>
  );
}
